import { useEffect, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { MessageSquare, Send } from "lucide-react";

export default function Reviews() {
  const [items, setItems] = useState([]);
  const [f, setF] = useState({ first_name: "", city: "", build_type: "", quote: "" });
  const [loading, setLoading] = useState(false);

  const load = () => api.get("/testimonials").then((r) => setItems(r.data));
  useEffect(() => {
    load();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!f.first_name || !f.city || !f.quote) {
      toast.error("Name, city and review are required.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/reviews", f);
      toast.success("Thanks! Your review is live.");
      setF({ first_name: "", city: "", build_type: "", quote: "" });
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to post. Try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="reviews"
      data-testid="reviews-section"
      className="py-24 lg:py-32 border-t border-neutral-900"
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-8 lg:px-12 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <p className="font-mono-label text-[11px] text-neutral-500 mb-4">
            07 · Leave a Review
          </p>
          <h2 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tighter">
            How was<br />your build?
          </h2>
          <p className="mt-6 text-neutral-400 max-w-md">
            Had a bay done by The Sim Guys? Drop a line — reviews land right here
            so everyone can see the real word-of-mouth.
          </p>

          <form onSubmit={submit} className="mt-8 grid grid-cols-2 gap-3">
            <label className="col-span-2 md:col-span-1 block">
              <span className="font-mono-label text-[10px] text-neutral-500">First name</span>
              <input
                required
                data-testid="review-input-name"
                value={f.first_name}
                maxLength={40}
                onChange={(e) => setF({ ...f, first_name: e.target.value })}
                className="w-full mt-1 bg-transparent border-b border-neutral-700 focus:border-[#F5F5F0] py-2 outline-none text-[#F5F5F0]"
              />
            </label>
            <label className="col-span-2 md:col-span-1 block">
              <span className="font-mono-label text-[10px] text-neutral-500">City</span>
              <input
                required
                data-testid="review-input-city"
                value={f.city}
                maxLength={60}
                onChange={(e) => setF({ ...f, city: e.target.value })}
                className="w-full mt-1 bg-transparent border-b border-neutral-700 focus:border-[#F5F5F0] py-2 outline-none text-[#F5F5F0]"
              />
            </label>
            <label className="col-span-2 block">
              <span className="font-mono-label text-[10px] text-neutral-500">Build type (optional)</span>
              <input
                data-testid="review-input-build"
                value={f.build_type}
                maxLength={60}
                placeholder="Basement bay, Garage conversion, etc."
                onChange={(e) => setF({ ...f, build_type: e.target.value })}
                className="w-full mt-1 bg-transparent border-b border-neutral-700 focus:border-[#F5F5F0] py-2 outline-none text-[#F5F5F0] placeholder:text-neutral-700"
              />
            </label>
            <label className="col-span-2 block">
              <span className="font-mono-label text-[10px] text-neutral-500">Your review</span>
              <textarea
                required
                rows="4"
                data-testid="review-input-quote"
                value={f.quote}
                maxLength={600}
                onChange={(e) => setF({ ...f, quote: e.target.value })}
                className="w-full mt-1 bg-transparent border-b border-neutral-700 focus:border-[#F5F5F0] py-2 outline-none text-[#F5F5F0] resize-none"
              />
            </label>
            <button
              type="submit"
              disabled={loading}
              data-testid="review-submit-btn"
              className="col-span-2 mt-2 inline-flex items-center justify-center gap-2 bg-[#F5F5F0] text-black py-3 font-semibold hover:bg-[#EAEAE5] disabled:opacity-50 transition-colors"
            >
              <Send size={14} />
              {loading ? "Posting…" : "Post my review"}
            </button>
          </form>
        </div>

        <div className="lg:col-span-7">
          <p className="font-mono-label text-[11px] text-neutral-500 mb-6">
            {items.length} {items.length === 1 ? "review" : "reviews"}
          </p>
          {items.length === 0 ? (
            <div
              data-testid="reviews-empty"
              className="border border-dashed border-neutral-800 p-10 flex flex-col items-center justify-center text-center"
            >
              <MessageSquare size={24} className="text-neutral-600 mb-3" />
              <p className="font-display font-semibold text-lg">
                Be the first to leave a review.
              </p>
              <p className="text-neutral-500 text-sm mt-2 max-w-sm">
                Freshly built site — real reviews from real clients coming soon.
              </p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 gap-4">
              {items.map((t) => (
                <div
                  key={t.id}
                  data-testid={`review-card-${t.id}`}
                  className="border border-neutral-900 p-5"
                >
                  <p className="text-sm text-neutral-200 leading-relaxed">
                    &ldquo;{t.quote}&rdquo;
                  </p>
                  <p className="mt-4 font-mono-label text-[10px] text-neutral-500">
                    {t.first_name} · {t.city}
                    {t.build_type ? ` · ${t.build_type}` : ""}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
